declare global {
  interface Window {
    webkitAudioContext: typeof AudioContext;
  }
}

const audioBufferCache = new Map<string, AudioBuffer>();
let _audioCtx: AudioContext | null = null;
let _masterGain: GainNode | null = null;

function getAudioCtx() {
  if (!_audioCtx)
    _audioCtx = new (window.AudioContext || window.webkitAudioContext)({
      latencyHint: "interactive",
    });

  if (!_masterGain) {
    _masterGain = _audioCtx.createGain();
    _masterGain.gain.value = 0.9; // headroom
    _masterGain.connect(_audioCtx.destination);
  }

  return _audioCtx;
}

export const ensureAudioContextStarted = async () => {
  if (typeof window === "undefined") return false;

  const ctx = getAudioCtx();

  // `interrupted` is Safari-specific.
  if (ctx.state === "suspended" || ctx.state === "interrupted") {
    try {
      await ctx.resume();
    } catch {}
  }

  return ctx.state === "running";
};

export const preloadBuffer = async (url: string) => {
  if (audioBufferCache.get(url)) return;

  const res = await fetch(url);
  const ab = await res.arrayBuffer();
  const newBuffer = await getAudioCtx().decodeAudioData(ab);

  audioBufferCache.set(url, newBuffer);
};

export const playSound = (
  url: string,
  volume: number,
  activeSources: Set<string>,
) => {
  const ctx = getAudioCtx();
  const vol = Math.min(Math.max(volume, 0), 1);
  const buffer = audioBufferCache.get(url);

  if (!buffer) return;

  const src = ctx.createBufferSource();
  src.buffer = buffer;
  // slight pitch variance for realism & phase decorrelation
  src.playbackRate.value = 1 + (Math.random() - 0.5) * 0.04;
  const gain = ctx.createGain();
  // optional perceptual curve (square root) could go here; keep linear for now
  gain.gain.value = vol;

  src.connect(gain);
  gain.connect(_masterGain!);

  const cleanup = () => {
    activeSources.delete(url);
    src.removeEventListener("ended", cleanup);
    src.disconnect();
    gain.disconnect();
  };

  src.addEventListener("ended", cleanup);
  activeSources.add(url);
  src.start(Math.max(ctx.currentTime, 0));
};
